/**
 * Twilio Voice Call subsystem (seeker side).
 *
 * Components:
 *  • <PreCallModal/>      — picks WebRTC vs PSTN, captures phone for PSTN.
 *  • <ActiveCallPanel/>   — full-screen mic+timer+hangup during an in-browser call.
 *  • <PSTNRingingModal/>  — "we're calling your phone" status while Twilio dials.
 *
 * Hook:
 *  • useTwilioVoice()     — wraps the @twilio/voice-sdk Device for one
 *    seeker session. Returns connect/disconnect helpers + live state.
 *
 * Geo-targeting:
 *  • US visitors  → PSTN choice hidden, WebRTC always.
 *  • IN visitors  → choose WebRTC OR PSTN.
 */

import React, { useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneSlash, Microphone, MicrophoneSlash, Globe, X, ShieldCheck } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

const fmtTimer = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/* ─── Hook: useTwilioVoice ────────────────────────────────────────────── */

export function useTwilioVoice({ role, userId, autoRegister = false }) {
  // Device + active call references kept off-state because mutating them
  // in setState would recreate the SDK objects and lose the WebRTC peer.
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(null); // { call, identity, started_at }
  const [muted, setMuted] = useState(false);

  // Issue a token and register the Device on demand. Call this from a
  // button handler — most browsers BLOCK SDK init until a user gesture.
  const ensureDevice = async () => {
    if (deviceRef.current) return deviceRef.current;
    const { data } = await api.post("/voice/access-token", { role, user_id: userId });
    const device = new Device(data.token, {
      logLevel: "warn",
      codecPreferences: ["opus", "pcmu"],
    });
    device.on("registered", () => setReady(true));
    device.on("error", (e) => {
      console.error("[Twilio Voice]", e);
      toast.error(e?.message || "Voice error. Try again.");
    });
    device.on("incoming", (call) => {
      // Astrologer side — incoming WebRTC ring.
      callRef.current = call;
      setActive({ call, identity: call.parameters.From, started_at: null, direction: "in" });
      // CRITICAL: attach disconnect/cancel listeners on the incoming call
      // right when it arrives. Without these, the seeker hanging up
      // leaves the astrologer's UI stuck showing "call ongoing" (Twilio
      // tears the SDK connection down, but our React state never
      // clears). Reported bug (Feb 2026 screenshot).
      const clearActive = () => {
        callRef.current = null;
        setActive(null);
        setMuted(false);
      };
      call.on("disconnect", clearActive);
      call.on("cancel", clearActive);
      call.on("reject", clearActive);
    });
    await device.register();
    deviceRef.current = device;
    return device;
  };

  // Place an outgoing WebRTC call to `targetIdentity` (e.g. astrologer-<id>)
  // `astrologerId` is required so we can log the call server-side for
  // billing. We call /voice/webrtc/start IMMEDIATELY (not on accept) so
  // even a 5-second hangup gets a doc — then /end fires from the
  // disconnect listener with the actual duration.
  const connect = async (targetIdentity, astrologerId) => {
    const device = await ensureDevice();
    let logCallId = null;
    let maxSeconds = null;
    if (astrologerId) {
      try {
        const { data } = await api.post("/voice/webrtc/start", { astrologer_id: astrologerId });
        logCallId = data.call_id;
        // Backend returns the seeker's affordable wall-clock window in
        // seconds. When it elapses we hard-hangup on this side (Twilio
        // has no equivalent of `time_limit` for <Client> dials). Guards
        // against runaway WebRTC calls when the seeker's wallet is
        // exhausted — reported bug (Feb 2026 screenshot).
        maxSeconds = Number.isFinite(data?.max_seconds_allowed) ? data.max_seconds_allowed : null;
      } catch (e) {
        // 402 = insufficient balance from the preflight check. Surface
        // the message so the UI can toast it — the call must NOT proceed.
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 402 && detail) {
          throw new Error(detail);
        }
        /* other errors: billing log is non-blocking */
      }
    }
    const call = await device.connect({ params: { To: targetIdentity } });
    callRef.current = call;
    const started = Date.now();
    setActive({ call, identity: targetIdentity, started_at: started, direction: "out" });

    const finalize = async () => {
      if (!logCallId) return;
      const seconds = Math.max(0, Math.round((Date.now() - started) / 1000));
      try {
        await api.post("/voice/webrtc/end", {
          astrologer_id: astrologerId,
          call_id: logCallId,
          duration_seconds: seconds,
        });
      } catch (e) { /* swallow — backend retries on next request */ }
    };
    // Auto-hangup timer — fires exactly at maxSeconds so a seeker with
    // ₹5 balance at ₹15/min gets cut off at 20 s (5/15*60 = 20) instead
    // of talking indefinitely at the platform's expense.
    let autoHangupTimer = null;
    if (maxSeconds && maxSeconds > 0) {
      autoHangupTimer = setTimeout(() => {
        try { callRef.current?.disconnect(); } catch (_) { /* already gone */ }
      }, maxSeconds * 1000);
    }
    const clearTimer = () => {
      if (autoHangupTimer) { clearTimeout(autoHangupTimer); autoHangupTimer = null; }
    };
    call.on("disconnect", () => {
      clearTimer();
      finalize();
      callRef.current = null;
      setActive(null);
      setMuted(false);
    });
    call.on("cancel", () => {
      clearTimer();
      finalize();
      callRef.current = null;
      setActive(null);
    });
    return call;
  };

  const accept = () => callRef.current?.accept();
  const reject = () => {
    callRef.current?.reject();
    callRef.current = null;
    setActive(null);
  };
  const hangup = () => {
    callRef.current?.disconnect();
    callRef.current = null;
    setActive(null);
    setMuted(false);
  };
  const toggleMute = () => {
    if (!callRef.current) return;
    const next = !muted;
    callRef.current.mute(next);
    setMuted(next);
  };

  useEffect(() => {
    if (autoRegister) ensureDevice().catch(() => {});
    return () => {
      try {
        callRef.current?.disconnect();
        deviceRef.current?.destroy();
      } catch { /* device already torn down */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRegister]);

  return { ready, active, muted, ensureDevice, connect, accept, reject, hangup, toggleMute };
}

/* ─── PreCallModal — picks the call mode ──────────────────────────────── */

export function PreCallModal({ astrologer, country, onClose, onPickWebRTC, onPickPSTN }) {
  // US visitors → PSTN choice hidden (per product spec).
  const isUS = (country || "").toUpperCase() === "US";
  const [mode, setMode] = useState(isUS ? "webrtc" : null);
  // Pre-fill from the seeker's last-used PSTN number (stored on this
  // device only — never sent to the backend until they actually start
  // a call). Saves them from re-typing on every call.
  const [phone, setPhone] = useState(() => {
    try { return localStorage.getItem("akc.seeker_pstn_phone") || ""; }
    catch { return ""; }
  });
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    if (mode === "webrtc") {
      setBusy(true);
      try { await onPickWebRTC(); } finally { setBusy(false); }
      return;
    }
    if (mode === "pstn") {
      const cleaned = phone.trim().replace(/\s/g, "");
      if (!/^\+[1-9]\d{6,15}$/.test(cleaned)) {
        toast.error("Enter your phone in international format (e.g. +919876543210)");
        return;
      }
      // Remember this number locally so the next call pre-fills it.
      try { localStorage.setItem("akc.seeker_pstn_phone", cleaned); } catch { /* quota / privacy mode — non-fatal */ }
      setBusy(true);
      try { await onPickPSTN(cleaned); } finally { setBusy(false); }
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4" data-testid="precall-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 p-6 sm:p-7">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
          data-testid="precall-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3">
          <img src={astrologer.avatar} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-amber-300" />
          <div>
            <p className="label-eyebrow">Calling</p>
            <h2 className="font-serif-display text-2xl text-[#3F2D1B]">{astrologer.name}</h2>
            <p className="text-xs text-muted">₹{astrologer.rate_per_min}/min</p>
          </div>
        </div>

        <p className="text-sm text-stone-700 mt-5">
          {isUS
            ? "We'll connect you over a secure in-app voice call."
            : "How would you like to take this call?"}
        </p>

        <div className={`grid ${isUS ? "grid-cols-1" : "grid-cols-2"} gap-3 mt-4`}>
          <button
            type="button"
            onClick={() => setMode("webrtc")}
            className={`rounded-xl border-2 px-4 py-4 text-left transition ${
              mode === "webrtc" ? "border-amber-500 bg-amber-50 shadow" : "border-stone-200 hover:border-amber-300"
            }`}
            data-testid="precall-mode-webrtc"
          >
            <div className="flex items-center gap-2">
              <Globe size={18} weight="fill" className="text-amber-700" />
              <p className="text-xs uppercase tracking-wide font-semibold text-amber-700">In-app call</p>
            </div>
            <p className="text-sm font-medium text-[#3F2D1B] mt-1.5">Best on Wi-Fi or 4G</p>
            <p className="text-[11px] text-muted mt-0.5">No phone number needed.</p>
          </button>

          {!isUS && (
            <button
              type="button"
              onClick={() => setMode("pstn")}
              className={`rounded-xl border-2 px-4 py-4 text-left transition ${
                mode === "pstn" ? "border-amber-500 bg-amber-50 shadow" : "border-stone-200 hover:border-amber-300"
              }`}
              data-testid="precall-mode-pstn"
            >
              <div className="flex items-center gap-2">
                <Phone size={18} weight="fill" className="text-amber-700" />
                <p className="text-xs uppercase tracking-wide font-semibold text-amber-700">Phone call</p>
              </div>
              <p className="text-sm font-medium text-[#3F2D1B] mt-1.5">We&rsquo;ll ring your phone</p>
              <p className="text-[11px] text-muted mt-0.5">Works on weak data signal.</p>
            </button>
          )}
        </div>

        {mode === "pstn" && (
          <div className="mt-4">
            <label className="label-eyebrow">Your phone number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              autoFocus
              className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none font-mono"
              data-testid="precall-phone-input"
            />
            <p className="text-[11px] text-muted mt-1.5 flex items-center gap-1">
              <ShieldCheck size={11} weight="fill" className="text-emerald-600" />
              Saved on your device for next time. Edit any time.
            </p>
          </div>
        )}

        <div className="rounded-md bg-amber-50/60 border border-amber-200 p-3 text-xs text-stone-700 leading-relaxed mt-4">
          You&rsquo;ll be charged{" "}
          <span className="font-semibold text-[#3F2D1B]">₹{astrologer.rate_per_min}/min</span>{" "}
          from your wallet, billed every 60 seconds. Hang up any time.
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 btn-ghost rounded-full py-3 text-sm"
            data-testid="precall-cancel"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={start}
            disabled={busy || !mode || (mode === "pstn" && !phone)}
            className="flex-1 btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="precall-start"
          >
            {busy ? "Connecting…" : (
              <>
                <Phone size={14} weight="fill" />
                Start call
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── ActiveCallPanel — in-progress WebRTC call ────────────────────────── */

export function ActiveCallPanel({ astrologer, muted, onMute, onHangup }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="fixed inset-0 z-[120] bg-gradient-to-br from-[#3F2D1B] via-[#2A1F12] to-[#1A1410] flex flex-col items-center justify-center px-6" data-testid="active-call-panel">
      <div className="absolute top-4 right-4 text-amber-300/60 text-xs font-mono">
        {fmtTimer(seconds)}
      </div>
      <img src={astrologer.avatar} alt="" className="w-32 h-32 rounded-full object-cover border-4 border-amber-300/60 shadow-2xl animate-pulse" />
      <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-100 mt-6">{astrologer.name}</h2>
      <p className="text-amber-200/70 text-sm mt-2" data-testid="active-call-timer">
        {fmtTimer(seconds)} · ₹{astrologer.rate_per_min}/min
      </p>
      <p className="text-emerald-300 text-xs mt-3 inline-flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        Connected
      </p>

      <div className="flex items-center gap-5 mt-12">
        <button
          onClick={onMute}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition ${muted ? "bg-amber-300 text-[#1A1410]" : "bg-white/10 hover:bg-white/20 text-amber-100"}`}
          data-testid="call-mute-btn"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? <MicrophoneSlash size={22} weight="fill" /> : <Microphone size={22} weight="fill" />}
        </button>
        <button
          onClick={onHangup}
          className="w-16 h-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-2xl transition"
          data-testid="call-hangup-btn"
          aria-label="Hang up"
        >
          <PhoneSlash size={26} weight="fill" />
        </button>
      </div>
    </div>
  );
}

/* ─── PSTNRingingModal — "we're calling your phone" ───────────────────── */

export function PSTNRingingModal({ astrologer, callId, onClose }) {
  const [status, setStatus] = useState("ringing");
  // Poll the call doc every 3s while ringing → in-progress → completed.
  useEffect(() => {
    if (!callId) return;
    let stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/voice/call/${callId}`);
        if (data?.status) setStatus(data.status);
        if (data?.status === "completed") {
          setTimeout(onClose, 1500);
          return;
        }
      } catch { /* ignore transient errors */ }
      if (!stop) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [callId, onClose]);

  return (
    <div className="fixed inset-0 z-[120] bg-gradient-to-br from-[#3F2D1B] via-[#2A1F12] to-[#1A1410] flex flex-col items-center justify-center px-6" data-testid="pstn-ringing-modal">
      <img src={astrologer.avatar} alt="" className="w-28 h-28 rounded-full object-cover border-4 border-amber-300/60 shadow-2xl animate-pulse" />
      <h2 className="font-serif-display text-3xl text-amber-100 mt-6">{astrologer.name}</h2>
      <p className="text-amber-200/70 text-sm mt-2 text-center max-w-xs leading-relaxed">
        {status === "ringing" && "📞 Calling your phone now. Pick up, then we'll dial the astrologer."}
        {status === "in-progress" && "Connected on your phone. You can close this window."}
        {status === "completed" && "Call ended. Thanks!"}
      </p>
      <button
        onClick={onClose}
        className="mt-8 px-6 py-2.5 rounded-full text-sm font-medium border border-amber-300/30 text-amber-100 hover:bg-white/10 transition"
        data-testid="pstn-close-btn"
      >
        Close
      </button>
    </div>
  );
}
